import { useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Switch } from "../../components/ui/switch";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../../components/ui/select";
import { Check, ChevronLeft, ChevronRight, Sparkles, CreditCard } from "lucide-react";
import { toast } from "sonner";

const CARD_TYPES = ["Visa", "Mastercard", "Verve", "American Express", "Discover", "UnionPay"];
const CATEGORIES = ["Debit", "Credit", "Prepaid"];
const CURRENCIES = ["USD", "EUR", "GBP", "NGN", "CAD", "AUD", "JPY", "ZAR", "KES", "GHS"];
const COUNTRIES = ["United States", "Nigeria", "United Kingdom", "Canada", "Germany", "France", "Ghana", "Kenya", "South Africa", "Australia", "India", "Other"];
const STATUSES = ["Active", "Inactive", "Blocked"];
const MONTHS = Array.from({ length: 12 }, (_, i) => String(i + 1).padStart(2, "0"));
const YEARS = Array.from({ length: 11 }, (_, i) => String(new Date().getFullYear() + i));

const STEPS = ["Card Type", "Parameters", "Settings", "Review"];

function luhnComplete(partial) {
  let digits = partial.replace(/\D/g, "").slice(0, 15);
  while (digits.length < 15) digits += Math.floor(Math.random() * 10);
  let sum = 0;
  const rev = digits.split("").reverse();
  for (let i = 0; i < rev.length; i++) {
    let d = parseInt(rev[i], 10);
    if (i % 2 === 0) { d *= 2; if (d > 9) d -= 9; }
    sum += d;
  }
  const check = (10 - (sum % 10)) % 10;
  return digits + check;
}

const initial = {
  card_type: "Visa", card_category: "Debit",
  bin: "", card_number: "", expiry_month: "01", expiry_year: String(new Date().getFullYear() + 3),
  cvv: "", cardholder_name: "", issuing_bank: "", currency: "USD",
  billing_address: { street: "", city: "", state: "", postal_code: "", country: "United states" },
  daily_limit: 1000, monthly_limit: 20000, international: true, online: true, atm: true, status: "Active",
};

export default function NewCard() {
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [saving, setSaving] = useState(false);
  const [f, setF] = useState(initial);

  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const setAddr = (k, v) => setF((p) => ({ ...p, billing_address: { ...p.billing_address, [k]: v } }));

  const autoGen = () => {
    const base = (f.bin || "400000").replace(/\D/g, "");
    set("card_number", luhnComplete(base));
    toast.success("Card number generated");
  };

  const canNext = () => {
    if (step === 0) return f.card_type && f.card_category;
    if (step === 1) return f.card_number.replace(/\D/g, "").length >= 12 && f.cvv && f.cardholder_name;
    return true;
  };

  const submit = async () => {
    setSaving(true);
    try {
      await api.post("/generated-cards", {
        ...f,
        daily_limit: Number(f.daily_limit) || 0,
        monthly_limit: Number(f.monthly_limit) || 0,
      });
      toast.success("Card generated & encrypted");
      navigate("/cards/mine");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const mask = (n) => { const d = n.replace(/\D/g, ""); return d ? "**** **** **** " + d.slice(-4) : "—"; };

  return (
    <div className="space-y-8">
      <div>
        <div className="text-xs font-mono uppercase tracking-wider text-primary">Card Generation</div>
        <h1 className="mt-1 text-3xl font-bold tracking-tight sm:text-4xl">New Card</h1>
      </div>

      <div className="flex items-center gap-2" data-testid="wizard-steps">
        {STEPS.map((s, i) => (
          <div key={s} className="flex flex-1 items-center">
            <div className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-xs font-semibold transition-colors ${
              i < step ? "bg-primary text-primary-foreground" : i === step ? "bg-primary/20 text-primary ring-2 ring-primary" : "bg-muted text-muted-foreground"}`}>
              {i < step ? <Check className="h-4 w-4" /> : i + 1}
            </div>
            <span className={`ml-2 hidden text-xs sm:inline ${i === step ? "font-medium text-foreground" : "text-muted-foreground"}`}>{s}</span>
            {i < STEPS.length - 1 && <div className={`mx-2 h-px flex-1 ${i < step ? "bg-primary" : "bg-border"}`} />}
          </div>
        ))}
      </div>

      <div className="rounded-xl border border-border bg-card p-6">
        {step === 0 && (
          <div className="grid gap-5 sm:grid-cols-2" data-testid="step-type">
            <div className="space-y-1.5">
              <Label>Card type</Label>
              <Select value={f.card_type} onValueChange={(v) => set("card_type", v)}>
                <SelectTrigger data-testid="wiz-card-type"><SelectValue /></SelectTrigger>
                <SelectContent>{CARD_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Card category</Label>
              <Select value={f.card_category} onValueChange={(v) => set("card_category", v)}>
                <SelectTrigger data-testid="wiz-card-category"><SelectValue /></SelectTrigger>
                <SelectContent>{CATEGORIES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
        )}

        {step === 1 && (
          <div className="space-y-5" data-testid="step-params">
            <div className="grid gap-5 sm:grid-cols-2">
              <div className="space-y-1.5">
                <Label>BIN</Label>
                <Input value={f.bin} onChange={(e) => set("bin", e.target.value)} placeholder="453211" data-testid="wiz-bin" />
              </div>
              <div className="space-y-1.5">
                <Label>Card number</Label>
                <div className="flex gap-2">
                  <Input value={f.card_number} onChange={(e) => set("card_number", e.target.value)} placeholder="Auto or manual" data-testid="wiz-card-number" />
                  <Button type="button" variant="outline" onClick={autoGen} data-testid="wiz-generate"><Sparkles className="h-4 w-4" /></Button>
                </div>
              </div>
              <div className="space-y-1.5">
                <Label>Expiry month</Label>
                <Select value={f.expiry_month} onValueChange={(v) => set("expiry_month", v)}>
                  <SelectTrigger data-testid="wiz-exp-month"><SelectValue /></SelectTrigger>
                  <SelectContent>{MONTHS.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>Expiry year</Label>
                <Select value={f.expiry_year} onValueChange={(v) => set("expiry_year", v)}>
                  <SelectTrigger data-testid="wiz-exp-year"><SelectValue /></SelectTrigger>
                  <SelectContent>{YEARS.map((y) => <SelectItem key={y} value={y}>{y}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>CVV / CVC</Label>
                <Input value={f.cvv} onChange={(e) => set("cvv", e.target.value)} placeholder="•••" maxLength={4} data-testid="wiz-cvv" />
                <p className="text-[11px] text-muted-foreground">Encrypted on save, never displayed again.</p>
              </div>
              <div className="space-y-1.5">
                <Label>Cardholder name</Label>
                <Input value={f.cardholder_name} onChange={(e) => set("cardholder_name", e.target.value)} placeholder="Jane Doe" data-testid="wiz-holder" />
              </div>
              <div className="space-y-1.5">
                <Label>Issuing bank</Label>
                <Input value={f.issuing_bank} onChange={(e) => set("issuing_bank", e.target.value)} placeholder="Acme Bank" data-testid="wiz-bank" />
              </div>
              <div className="space-y-1.5">
                <Label>Currency</Label>
                <Select value={f.currency} onValueChange={(v) => set("currency", v)}>
                  <SelectTrigger data-testid="wiz-currency"><SelectValue /></SelectTrigger>
                  <SelectContent>{CURRENCIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div className="border-t border-border pt-4">
              <div className="mb-3 text-xs font-mono uppercase tracking-wider text-muted-foreground">Billing address</div>
              <div className="grid gap-5 sm:grid-cols-2">
                <div className="space-y-1.5 sm:col-span-2">
                  <Label>Street address</Label>
                  <Input value={f.billing_address.street} onChange={(e) => setAddr("street", e.target.value)} data-testid="wiz-street" />
                </div>
                <div className="space-y-1.5"><Label>City</Label><Input value={f.billing_address.city} onChange={(e) => setAddr("city", e.target.value)} data-testid="wiz-city" /></div>
                <div className="space-y-1.5"><Label>State</Label><Input value={f.billing_address.state} onChange={(e) => setAddr("state", e.target.value)} data-testid="wiz-state" /></div>
                <div className="space-y-1.5"><Label>Postal code</Label><Input value={f.billing_address.postal_code} onChange={(e) => setAddr("postal_code", e.target.value)} data-testid="wiz-postal" /></div>
                <div className="space-y-1.5">
                  <Label>Country</Label>
                  <Select value={f.billing_address.country} onValueChange={(v) => setAddr("country", v)}>
                    <SelectTrigger data-testid="wiz-country"><SelectValue /></SelectTrigger>
                    <SelectContent>{COUNTRIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>
            </div>
          </div>
        )}

        {step === 2 && (
          <div className="space-y-5" data-testid="step-settings">
            <div className="grid gap-5 sm:grid-cols-2">
              <div className="space-y-1.5">
                <Label>Daily transaction limit</Label>
                <Input type="number" value={f.daily_limit} onChange={(e) => set("daily_limit", e.target.value)} data-testid="wiz-daily" />
              </div>
              <div className="space-y-1.5">
                <Label>Monthly transaction limit</Label>
                <Input type="number" value={f.monthly_limit} onChange={(e) => set("monthly_limit", e.target.value)} data-testid="wiz-monthly" />
              </div>
            </div>
            {[["international", "International transactions"], ["online", "Online transactions"], ["atm", "ATM withdrawals"]].map(([k, label]) => (
              <div key={k} className="flex items-center justify-between rounded-lg border border-border px-4 py-3">
                <span className="text-sm font-medium">{label}</span>
                <Switch checked={f[k]} onCheckedChange={(v) => set(k, v)} data-testid={`wiz-${k}`} />
              </div>
            ))}
            <div className="space-y-1.5">
              <Label>Card status</Label>
              <Select value={f.status} onValueChange={(v) => set("status", v)}>
                <SelectTrigger data-testid="wiz-status"><SelectValue /></SelectTrigger>
                <SelectContent>{STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
        )}

        {step === 3 && (
          <div className="space-y-4" data-testid="step-review">
            <div className="flex items-center gap-2 text-sm font-medium text-primary"><CreditCard className="h-4 w-4" /> Review before saving</div>
            <div className="grid gap-x-8 gap-y-2 text-sm sm:grid-cols-2">
              {[
                ["Card type", f.card_type], ["Category", f.card_category], ["BIN", f.bin || "—"],
                ["Number", mask(f.card_number)], ["Expiry", `${f.expiry_month}/${f.expiry_year}`],
                ["CVV", "••• (encrypted)"], ["Holder", f.cardholder_name], ["Issuing bank", f.issuing_bank || "—"],
                ["Currency", f.currency], ["Daily limit", f.daily_limit], ["Monthly limit", f.monthly_limit],
                ["International", f.international ? "On" : "Off"], ["Online", f.online ? "On" : "Off"],
                ["ATM", f.atm ? "On" : "Off"], ["Status", f.status],
              ].map(([k, v]) => (
                <div key={k} className="flex justify-between gap-4 border-b border-border/60 pb-1.5">
                  <span className="text-muted-foreground">{k}</span><span className="font-medium text-right">{v}</span>
                </div>
              ))}
            </div>
            <div>
              <div className="mb-1 text-xs font-mono uppercase tracking-wider text-muted-foreground">Billing address</div>
              <div className="text-sm">{[f.billing_address.street, f.billing_address.city, f.billing_address.state, f.billing_address.postal_code, f.billing_address.country].filter(Boolean).join(", ") || "—"}</div>
            </div>
          </div>
        )}

        <div className="mt-6 flex items-center justify-between border-t border-border pt-5">
          <Button variant="ghost" disabled={step === 0} onClick={() => setStep(step - 1)} data-testid="wiz-back">
            <ChevronLeft className="mr-1 h-4 w-4" /> Back
          </Button>
          {step < STEPS.length - 1 ? (
            <Button disabled={!canNext()} onClick={() => setStep(step + 1)} data-testid="wiz-next">
              Next <ChevronRight className="ml-1 h-4 w-4" />
            </Button>
          ) : (
            <Button disabled={saving} onClick={submit} data-testid="wiz-submit">
              {saving ? "Saving…" : "Confirm & Generate"}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
